import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import { GetAnnouncementsResponseDto, MarkAnnouncementsSeenRequestDto } from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';

type MarkSeenParams = MarkAnnouncementsSeenRequestDto & {
  userId: string;
};

type Params = {
  announcementIds: string[];
  enabled?: boolean;
  userId: string;
};

type Return = {
  announcementsSeen?: GetAnnouncementsResponseDto;
  isGettingAnnouncementsSeen: boolean;
  isMarkingAnnouncementsSeen: boolean;
  markAnnouncementsSeen: (params: MarkSeenParams) => Promise<void>;
};

export const useAnnouncementsApiClient = ({ announcementIds, enabled, userId }: Params): Return => {
  const { appAnnouncementsApi } = useApiClient();
  const queryClient = useQueryClient();
  const isQueryEnabled = (enabled ?? true) && announcementIds.length > 0 && !!userId;

  const { data, isFetching } = useQuery<GetAnnouncementsResponseDto, AxiosError>({
    enabled: isQueryEnabled,
    meta: { disableSuccessAlert: true },
    queryFn: async () => {
      const response = await appAnnouncementsApi.getAppAnnouncements(userId, announcementIds);
      return response.data;
    },
    queryKey: [QueryKeyEnum.ANNOUNCEMENTS.SEEN, userId, ...announcementIds],
  });

  const { isPending, mutateAsync } = useMutation<void, AxiosError, MarkSeenParams>({
    meta: { disableSuccessAlert: true },
    mutationFn: async ({ announcementIds: ids, userId: currentUserId }) => {
      await appAnnouncementsApi.markAnnouncementsSeen(currentUserId, {
        announcementIds: ids,
      });
    },
    mutationKey: [QueryKeyEnum.ANNOUNCEMENTS.MARK_SEEN, userId],
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.ANNOUNCEMENTS.SEEN, userId],
      });
    },
  });

  return {
    announcementsSeen: data,
    isGettingAnnouncementsSeen: isFetching,
    isMarkingAnnouncementsSeen: isPending,
    markAnnouncementsSeen: async params => {
      await mutateAsync(params);
    },
  };
};
